import { useCallback, useState } from 'react';
import { googleSignIn } from '@/lib/auth.client';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';

const GoogleIcon: React.FC = () => {
  return (
    <div className="w-6 h-6 relative">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          fill="#999999"
        />
        <path
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          fill="#999999"
        />
        <path
          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          fill="#999999"
        />
        <path
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          fill="#999999"
        />
      </svg>
    </div>
  );
};

interface LoginFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  onSubmit: () => void;
  isBusy: boolean;
  ctaLabel: string;
  error?: string;
}

export default function LoginForm({ email, onEmailChange, onSubmit, isBusy, ctaLabel, error }: LoginFormProps) {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);

    try {
      await googleSignIn();
    } catch (error) {
      console.log('Error from logging form handleGoogleSignIn: ', error);
      setIsGoogleLoading(false);
    }
  };

  const handleEmailSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSubmit();
    },
    [onSubmit],
  );

  return (
    <div className="w-full font-inter-display">
      {/* Sign Up Form */}
      <div className="space-y-6">
        {/* Google Sign In */}
        <div className="space-y-2.5">
          {/* <Form action="/auth/google" method="post"> */}
          <button
            type="button"
            disabled={isGoogleLoading}
            className={`w-full bg-[#222222] border border-[#E5E5E5] rounded-xl py-3 sm:py-3 px-4 flex items-center justify-center gap-2.5 hover:bg-[#333333] active:scale-[0.98] transition-colors min-h-[48px] sm:min-h-auto ${isGoogleLoading ? 'opacity-80 cursor-not-allowed' : ''}`}
            onClick={handleGoogleSignIn}
          >
            {isGoogleLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <GoogleIcon />
            )}

            <span className="text-white text-base leading-[1.44em] font-neue-montreal">
              {isGoogleLoading ? '' : 'Continue with Google'}
            </span>
          </button>
          {/* </Form> */}

          {/* comment out email form for production push for the time being */}

          {/* Divider */}
          <div className="flex items-center gap-2 py-2">
            <div className="flex-1 h-px bg-[#EEEEEE]"></div>
            <span className="text-[#888888] text-xs leading-[1.2em] font-semibold" style={{ fontFamily: 'Inter' }}>
              OR
            </span>
            <div className="flex-1 h-px bg-[#EEEEEE]"></div>
          </div>
          {/* </div> */}

          {/* Email Form */}
          <div className="space-y-5" style={{ fontFamily: 'Neue Montreal' }}>
            <form onSubmit={handleEmailSubmit} className="space-y-3">
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => onEmailChange(e.target.value)}
                    className={`bg-[#F3F3F3] w-full rounded-xl py-3 px-4 pr-12 text-[#333333] text-base font-medium leading-[1.2em] min-h-[48px] sm:min-h-auto transition-colors placeholder:text-[#333333]/50 border ${
                      error ? 'border-[#FF0000] focus:border-[#FF0000]' : 'border-[#333333]/5 focus:border-[#0099FF]'
                    } focus:outline-none`}
                    aria-invalid={!!error}
                  />
                  {/* Exclamation icon for errors */}
                  {error && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
                      <div className="shrink-0 ml-1">
                        <div className="i-ph:warning-circle-fill text-[#FF0000] text-2xl" />
                      </div>
                    </div>
                  )}
                  {/* <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <MailOpen className="w-5 h-5 text-[#908B8B]" />
                  </div> */}
                </div>
              </div>

              {error && <p className="text-[#FF796E] text-sm font-normal leading-[1.2em]">{error}</p>}

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={!email || isBusy}
                  className={`w-full rounded-xl py-3 sm:py-2.5 px-4 shadow-sm transition-colors min-h-[48px] sm:min-h-auto border ${
                    email && !isBusy ? 'bg-white border-[#222] hover:bg-[#F3F3F3]' : 'bg-[#F3F3F3] cursor-not-allowed'
                  }`}
                >
                  <span
                    className={`text-base font-medium leading-[1.5em] tracking-[0.02em] ${
                      email && !isBusy ? 'text-[#222222]' : 'text-[#4C4D52]'
                    }`}
                  >
                    {ctaLabel}
                  </span>
                </button>

                <p className="text-center text-[#8C8C8C] text-[13px] font-normal leading-[1.2em]">
                  By proceeding you agree to Dualite's{' '}
                  <a
                    href="https://dualite.dev/legal/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#333333] font-medium"
                  >
                    Terms of Use
                  </a>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
